'use client'

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'

import { usePathname, useSearchParams } from 'next/navigation'

import { AMPLITUDE_API_KEY, APP_ENV, APP_ENVS } from '@/constants/env'
import { UserSession } from '@/types/user'

import * as amplitude from './amplitude.sdk'
import { amplitudeEvents } from './constants'
import type {
  CommonProperties,
  CreateMaestroConversationProperties,
  DashboardClickedProperties,
  DashboardLoadedProperties,
  FilterAppliedProperties,
  OpenMaestroConversationProperties,
  SendMaestroPromptProperties,
  TrackDashboardLoadedParams,
  TrackDownloadCsvDrilldownParams,
  TrackDownloadCsvParams,
  TrackDownloadImageParams,
  TrackFilterAppliedParams,
  TrackSectionClickedParams,
  UserLoggedInProperties,
} from './types'
import {
  getKeyFiltersFromFiltersJson,
  getKeyFiltersFromURL,
  getPropertiesFromURL,
} from './utils'

type AmplitudeContextType = {
  trackDashboardDataCompleted: () => void
  trackDashboardLoaded: (params: TrackDashboardLoadedParams) => void
  trackDownloadCsv: (params: TrackDownloadCsvParams) => void
  trackDownloadCsvDrilldown: (params: TrackDownloadCsvDrilldownParams) => void
  trackDownloadImage: (params: TrackDownloadImageParams) => void
  trackFilterApplied: (params: TrackFilterAppliedParams) => void
  trackSectionClicked: (params: TrackSectionClickedParams) => void
  trackUserLoggedIn: () => void
  trackUserLogout: () => void
  trackSupportClicked: () => void
  trackOpenMaestroConversation: (conversationId: string) => void
  trackCreateMaestroConversation: (conversationId: string) => void
  trackSendMaestroPrompt: (conversationId: string) => void
}

const AmplitudeContext = createContext<AmplitudeContextType>({
  trackDashboardDataCompleted: () => {},
  trackDashboardLoaded: () => {},
  trackFilterApplied: () => {},
  trackSectionClicked: () => {},
  trackSupportClicked: () => {},
  trackUserLoggedIn: () => {},
  trackUserLogout: () => {},
  trackDownloadCsv: () => {},
  trackDownloadCsvDrilldown: () => {},
  trackDownloadImage: () => {},
  trackOpenMaestroConversation: () => {},
  trackCreateMaestroConversation: () => {},
  trackSendMaestroPrompt: () => {},
})

type AmplitudeProps = {
  children: React.ReactNode
  userSession: UserSession
}

const AmplitudeProvider = (props: AmplitudeProps) => {
  const { children, userSession } = props
  const [isFirstLoad, setIsFirstLoad] = useState(true)
  const [url, setUrl] = useState<string | undefined>()
  const searchParams = useSearchParams()
  const pathname = usePathname()
  const isAmplitudeEnabled = APP_ENV === APP_ENVS.PRODUCTION
  const datagroupType = userSession.datagroup.type

  useEffect(() => {
    setUrl(`${pathname}?${decodeURI(searchParams.toString())}`)
  }, [pathname, searchParams])

  const trackUserLoggedIn = useCallback(() => {
    if (!isAmplitudeEnabled) return

    amplitude.track(amplitudeEvents.USER_LOGGED_IN, {
      timestamp: Date.now(),
    } satisfies UserLoggedInProperties)
  }, [isAmplitudeEnabled])

  const trackUserLogout = useCallback(() => {
    if (!isAmplitudeEnabled) return
    if (!url) return
    const eventProperties = getPropertiesFromURL(url, datagroupType)
    amplitude.track(amplitudeEvents.USER_LOGOUT, {
      ...eventProperties,
    } satisfies CommonProperties)
  }, [datagroupType, isAmplitudeEnabled, url])

  const trackSectionClicked = useCallback(
    (params: TrackSectionClickedParams) => {
      if (!isAmplitudeEnabled) return
      if (!url) return

      const { group, section } = params
      const eventProperties = getPropertiesFromURL(url, datagroupType)
      amplitude.track(amplitudeEvents.SECTION_CLICKED, {
        ...eventProperties,
        group,
        section,
        visualization_tech: 'Embeddable',
      } satisfies DashboardClickedProperties)
    },
    [datagroupType, isAmplitudeEnabled, url],
  )

  const trackDashboardLoaded = useCallback(
    (params: TrackDashboardLoadedParams) => {
      if (!isAmplitudeEnabled) return
      if (!url) return

      const { visualizationTech, group, section } = params
      const eventProperties = getPropertiesFromURL(url, datagroupType)
      const filterProperties = getKeyFiltersFromURL(url, datagroupType)
      amplitude.track(amplitudeEvents.DASHBOARD_LOADED, {
        ...eventProperties,
        ...filterProperties,
        group,
        section,
        trigger_type: isFirstLoad ? 'auto' : 'manual',
        visualization_tech: visualizationTech,
      } satisfies DashboardLoadedProperties)

      setIsFirstLoad(false)
    },
    [datagroupType, isAmplitudeEnabled, url, isFirstLoad],
  )

  const trackDashboardDataCompleted = useCallback(() => {
    if (!isAmplitudeEnabled) return
    if (!url) return

    const eventProperties = getPropertiesFromURL(url, datagroupType)
    amplitude.track(amplitudeEvents.DASHBOARD_DATA_COMPLETED, {
      ...eventProperties,
    } satisfies CommonProperties)
  }, [datagroupType, isAmplitudeEnabled, url])

  const trackFilterApplied = useCallback(
    (params: TrackFilterAppliedParams) => {
      if (!isAmplitudeEnabled) return
      if (!url) return

      const { filters, group, section } = params
      const eventProperties = getPropertiesFromURL(url, datagroupType)
      const { cleanedFilters, ...filterProperties } = getKeyFiltersFromFiltersJson(
        filters,
        datagroupType,
      )
      amplitude.track(amplitudeEvents.FILTER_APPLIED, {
        ...eventProperties,
        ...filterProperties,
        group,
        section,
        filters: cleanedFilters,
      } satisfies FilterAppliedProperties)
    },
    [datagroupType, isAmplitudeEnabled, url],
  )

  const trackSupportClicked = useCallback(() => {
    if (!isAmplitudeEnabled) return
    if (!url) return
    const eventProperties = getPropertiesFromURL(url, datagroupType)
    amplitude.track(amplitudeEvents.SUPPORT_CLICKED, {
      ...eventProperties,
    } satisfies CommonProperties)
  }, [datagroupType, isAmplitudeEnabled, url])

  const trackDownloadImage = useCallback(
    (params: TrackDownloadImageParams) => {
      if (!isAmplitudeEnabled) return
      if (!url) return
      const { group, section } = params
      const eventProperties = getPropertiesFromURL(url, datagroupType)
      amplitude.track(amplitudeEvents.DOWNLOAD_IMAGE, {
        ...eventProperties,
        group,
        section,
      } satisfies CommonProperties)
    },
    [datagroupType, isAmplitudeEnabled, url],
  )

  const trackDownloadCsv = useCallback(
    (params: TrackDownloadCsvParams) => {
      if (!isAmplitudeEnabled) return
      if (!url) return
      const { group, section } = params
      const eventProperties = getPropertiesFromURL(url, datagroupType)
      amplitude.track(amplitudeEvents.DOWNLOAD_CSV, {
        ...eventProperties,
        group,
        section,
      } satisfies CommonProperties)
    },
    [datagroupType, isAmplitudeEnabled, url],
  )

  const trackDownloadCsvDrilldown = useCallback(
    (params: TrackDownloadCsvDrilldownParams) => {
      if (!isAmplitudeEnabled) return
      if (!url) return
      const { group, section } = params
      const eventProperties = getPropertiesFromURL(url, datagroupType)
      amplitude.track(amplitudeEvents.DOWNLOAD_CSV_DRILLDOWN, {
        ...eventProperties,
        group,
        section,
      } satisfies CommonProperties)
    },
    [datagroupType, isAmplitudeEnabled, url],
  )

  const trackCreateMaestroConversation = useCallback(
    (conversationId: string) => {
      if (!isAmplitudeEnabled) return
      if (!url) return

      const { application, timestamp } = getPropertiesFromURL(url, datagroupType)
      amplitude.track(amplitudeEvents.CONVERSATION_STARTED, {
        application,
        timestamp,
        conversation_id: conversationId,
      } satisfies CreateMaestroConversationProperties)
    },
    [datagroupType, isAmplitudeEnabled, url],
  )

  const trackOpenMaestroConversation = useCallback(
    (conversationId: string) => {
      if (!isAmplitudeEnabled) return
      if (!url) return
      const { application, timestamp } = getPropertiesFromURL(url, datagroupType)
      amplitude.track(amplitudeEvents.CONVERSATION_OPENED, {
        application,
        timestamp,
        conversation_id: conversationId,
      } satisfies OpenMaestroConversationProperties)
    },
    [datagroupType, isAmplitudeEnabled, url],
  )

  const trackSendMaestroPrompt = useCallback(
    (conversationId: string) => {
      if (!isAmplitudeEnabled) return
      if (!url) return

      const { application, timestamp } = getPropertiesFromURL(url, datagroupType)
      amplitude.track(amplitudeEvents.CONVERSATION_PROMPT_SENT, {
        application,
        timestamp,
        conversation_id: conversationId,
      } satisfies SendMaestroPromptProperties)
    },
    [datagroupType, isAmplitudeEnabled, url],
  )

  useEffect(() => {
    if (!isAmplitudeEnabled) return
    if (!AMPLITUDE_API_KEY) throw new Error('AMPLITUDE_API_KEY is not defined')

    const userId = userSession.userAccount?.externalUserIdentifierId

    if (!userId) {
      console.warn(
        'Amplitude: User external user identifier id is not defined. Skipping amplitude tracking.',
      )
      return
    }

    amplitude.initUser(AMPLITUDE_API_KEY, userId, userSession)

    trackUserLoggedIn()
  }, [userSession, trackUserLoggedIn, isAmplitudeEnabled])

  const value = useMemo(
    () => ({
      trackUserLoggedIn,
      trackSectionClicked,
      trackDashboardLoaded,
      trackDashboardDataCompleted,
      trackFilterApplied,
      trackUserLogout,
      trackSupportClicked,
      trackDownloadCsv,
      trackDownloadCsvDrilldown,
      trackDownloadImage,
      trackOpenMaestroConversation,
      trackCreateMaestroConversation,
      trackSendMaestroPrompt,
    }),
    [
      trackUserLoggedIn,
      trackSectionClicked,
      trackDashboardLoaded,
      trackDashboardDataCompleted,
      trackFilterApplied,
      trackUserLogout,
      trackSupportClicked,
      trackDownloadCsv,
      trackDownloadCsvDrilldown,
      trackDownloadImage,
      trackOpenMaestroConversation,
      trackCreateMaestroConversation,
      trackSendMaestroPrompt,
    ],
  )

  return <AmplitudeContext.Provider value={value}>{children}</AmplitudeContext.Provider>
}

const useAmplitude = () => {
  const context = useContext(AmplitudeContext)
  if (context === undefined) {
    throw new Error('useAmplitude must be used within an AmplitudeProvider')
  }
  return context
}

export { useAmplitude }
export default AmplitudeProvider
